<template>
  <article v-if="spinLoading" class>
    <overlay :transparentBg="false" />
    <div class="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
      <section class="container">
        <section class="centerCss">
          <!-- Vue3Lottie assumed available in your project -->
          <Vue3Lottie
            :animationData="LoaderNormal"
            :speed="1"
            :loop="true"
            :height="isMobile ? 175 : 220"
            :width="isMobile ? 175 : 220"
          />
          <div class="textSection">
            <div :class="dynamicMsg ? 'label-text-medium text-center': 'textCss text-base text-textSecondary'">
              <div  class="spinner">
                {{ textSpinner }} <br/> {{ subTextSpinner }}
                <div class="bounce1"></div>
                <div class="bounce2"></div>
                <div class="bounce3"></div>
              </div>
              <p v-if="secondaryText" class="label-text-medium">{{ secondaryText }}</p>
            </div>
          </div>
        </section>
      </section>
    </div>

    <!-- dynamic messages area -->
    <span v-if="dynamicMsg" class="grid pb-10"></span>

    <!-- show only one line at a time with animation -->
    <section v-if="dynamicMsg" class="footer pb-10">
      <transition name="fade-slide" mode="out-in">
        <p
          v-if="currentLine"
          :key="currentIndex"
          class="animated-line label-text-myriad"
          aria-live="polite"
        >
          {{ currentLine }}
        </p>
      </transition>
    </section>
  </article>
</template>

<script>
import { mapGetters } from 'vuex';
import Overlay from './Overlay.vue';
import LoaderNormal from '../assets/animation/Loader_normal.json';

export default {
  name: 'SpinLoader',
  components: { Overlay },
  props: {
    spinLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    dynamicMsg: {
      type: Boolean,
      required: false,
      default: false,
    },
    textSpinner: {
      type: String,
      required: false,
      default: 'Please wait while we fetch',
    },
    subTextSpinner: {
      type: String,
      required: false,
      default: 'your details',
    },
    secondaryText: {
      type: String,
      required: false,
      default: '',
    },
    intervalMs: {
      type: Number,
      default: 5000,
    },
    loop: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      LoaderNormal,
      isMobile: false,
      isLoading: false,
      creditStatementData: undefined,
      allLines: [
        "Counting how many biscuit packets you could stock with this loan…",
        "Checking faster than your customer asking, “Bhaiya, aur discount?”",
        "Making sure your dukaan gets the working capital it deserves.",
        "Don’t worry — your Aadhaar and data are fully secure with us.",
        "Almost done — quicker than restocking your top-selling item!",
        "Reviewing your details — quick and smooth.",
        "Preparing your loan — smart, quick, and reliable.",
        "Ready! Your loan decision will flash on screen any moment."
      ],
      currentIndex: 0,
      timerId: null,
    };
  },
  computed: {
    ...mapGetters(['creditAccountInfo']),
    currentLine() {
      if (!this.allLines || this.allLines.length === 0) return '';
      return this.allLines[this.currentIndex] ?? '';
    }
  },
  mounted() {
    // detect mobile once
    this.isMobile = window.innerWidth <= 768;
    // start if spin is active and dynamic messages enabled
    if (this.spinLoading && this.dynamicMsg) {
      this.startRotator();
    }
    // listen to resize to update isMobile (optional)
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    this.stopRotator();
    window.removeEventListener('resize', this.handleResize);
  },
  watch: {
    // start/stop when spinLoading toggles
    spinLoading(newVal) {
      if (newVal && this.dynamicMsg) {
        this.startRotator();
      } else {
        this.stopRotator();
      }
    },
    // if dynamicMsg toggles while spinLoading true
    dynamicMsg(newVal) {
      if (newVal && this.spinLoading) {
        this.startRotator();
      } else {
        this.stopRotator();
      }
    }
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth <= 768;
    },

    startRotator() {
      if (this.timerId) return;
      if (this.currentIndex >= this.allLines.length) {
        this.currentIndex = 0;
      }
      this.timerId = setInterval(() => {
        const nextIndex = this.currentIndex + 1;
        if (nextIndex < this.allLines.length) {
          this.currentIndex = nextIndex;
        } else if (this.loop) {
          this.currentIndex = 0;
        } else {
          this.stopRotator();
        }
      }, this.intervalMs);
    },
    stopRotator() {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
  .loader {
    border: 8px solid #F3F3F3;
    border-radius: 50%;
    border-top: 8px solid #006295;
    width: 50px;
    height: 50px;
    -webkit-animation: spin 2s linear infinite; /* Safari */
    animation: spin 2s linear infinite;
  }
  /* Safari */
  @-webkit-keyframes spin {
    0% { -webkit-transform: rotate(0deg); }
    100% { -webkit-transform: rotate(360deg); }
  }
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  .textCss{
    font-family: 'Myriad Pro';
    font-style: normal;
    font-weight: 400;
    text-align: center;
    padding-top: 0.5rem;
  }
  .spinner > div {
    width: 4px;
    height: 4px;
    background-color: #041D28;
    border-radius: 100%;
    display: inline-block;
    -webkit-animation: sk-bouncedelay 1.4s infinite ease-in-out both;
    animation: sk-bouncedelay 1.4s infinite ease-in-out both;
  }

  .spinner .bounce1 {
    -webkit-animation-delay: -0.32s;
    animation-delay: -0.32s;
  }

  .spinner .bounce2 {
    -webkit-animation-delay: -0.16s;
    animation-delay: -0.16s;
  }

  @-webkit-keyframes sk-bouncedelay {
    0%, 80%, 100% { -webkit-transform: scale(0) }
    40% { -webkit-transform: scale(1.0) }
  }

  @keyframes sk-bouncedelay {
    0%, 80%, 100% {
      -webkit-transform: scale(0);
      transform: scale(0);
    } 40% {
      -webkit-transform: scale(1.0);
      transform: scale(1.0);
    }
  }
  .textSection{
    display: flex;
    width: max-content;
  }
  .container{
    display: grid;
    justify-items: center;
    position: relative;
    font-family: 'Myriad Pro';
  }
  .centerCss{
    top: 50%;
    display: grid;
    justify-items: center;
  }
  .iconSection{
    display: flex;
    margin-left: -25px;
  }
  .icon{
    margin-top: 15px;
    margin-left: -38px;
  }

  /* animation for one-line rotator */
  .fade-slide-enter-active,
  .fade-slide-leave-active {
    transition: opacity 280ms ease, transform 280ms ease;
  }
  .fade-slide-enter-from {
    opacity: 0;
    transform: translateY(8px);
  }
  .fade-slide-enter-to {
    opacity: 1;
    transform: translateY(0);
  }
  .fade-slide-leave-from {
    opacity: 1;
    transform: translateY(0);
  }
  .fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-8px);
  }

  .animated-line { margin: 0; text-align: center; }
</style>
